import { useEffect, useMemo } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, Circle, Polyline, Tooltip } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { optimisedRouteToPositions } from '../../utils/optimisedRoute'

export interface ThermalLocation {
  id?: string
  lat: number
  lon: number
  avgClimbRate: number
  maxClimbRate?: number
  count?: number
  name?: string
  altitude?: number
}

export interface TaskTurnpoint {
  lat: number
  lon: number
  radius: number
  name?: string
  description?: string
  type?: 'TAKEOFF' | 'SSS' | 'ESS'
}

interface ThermalMapProps {
  thermals: ThermalLocation[]
  turnpoints?: TaskTurnpoint[]
  center?: [number, number]
  zoom?: number
}

// Color scale for climb rate (m/s)
function getClimbColor(climbRate: number): string {
  if (climbRate < 0) return '#3b82f6'   // blue - sink
  if (climbRate < 1) return '#a3e635'   // lime - weak
  if (climbRate < 2) return '#22c55e'   // green - moderate
  if (climbRate < 3) return '#f59e0b'   // amber - strong
  if (climbRate < 4) return '#ef4444'   // red - very strong
  return '#7c3aed'                      // violet - extreme
}

function getClimbLabel(climbRate: number): string {
  if (climbRate < 0) return 'Sink'
  if (climbRate < 1) return 'Weak'
  if (climbRate < 2) return 'Moderate'
  if (climbRate < 3) return 'Strong'
  if (climbRate < 4) return 'Very Strong'
  return 'Extreme'
}

// Adjust map bounds to fit the optimised route + thermals
function MapBoundsAdjuster({
  thermals,
  routePoints,
}: {
  thermals: ThermalLocation[]
  routePoints?: [number, number][] | null
}) {
  const map = useMap()

  useEffect(() => {
    const points: [number, number][] = []
    // Prefer optimised route points for tighter framing around the flyable path
    if (routePoints && routePoints.length > 0) {
      for (const p of routePoints) points.push(p)
    }
    for (const t of thermals) points.push([t.lat, t.lon])
    if (points.length === 0) return
    map.fitBounds(points, { padding: [30, 30], maxZoom: 13 })
  }, [thermals, routePoints, map])

  return null
}

function turnpointColor(tp: TaskTurnpoint, idx: number, last: number): string {
  if (tp.type === 'TAKEOFF') return '#9ca3af'
  if (tp.type === 'SSS') return '#22c55e'
  if (tp.type === 'ESS' || idx === last) return '#ef4444'
  return '#2563eb'
}

const LEGEND_ITEMS = [
  { label: 'Sink (< 0)', color: '#3b82f6' },
  { label: 'Weak (0–1)', color: '#a3e635' },
  { label: 'Moderate (1–2)', color: '#22c55e' },
  { label: 'Strong (2–3)', color: '#f59e0b' },
  { label: 'Very Strong (3–4)', color: '#ef4444' },
  { label: 'Extreme (4+)', color: '#7c3aed' },
]

export default function ThermalMap({ thermals, turnpoints, center = [46.0, 10.0], zoom = 9 }: ThermalMapProps) {
  // Compute the optimised route (shortest path touching cylinder edges) rather
  // than naive center-to-center lines. Memoised so it only recalculates when
  // turnpoints change.
  const taskRoute: [number, number][] | null = useMemo(() => {
    if (!turnpoints || turnpoints.length < 2) return null
    return optimisedRouteToPositions(
      turnpoints.map((tp) => ({
        lat: tp.lat,
        lon: tp.lon,
        radius: tp.radius,
        type: tp.type,
      })),
    )
  }, [turnpoints])

  const lastTpIdx = (turnpoints?.length ?? 0) - 1

  return (
    <div className="relative w-full">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '500px', width: '100%', borderRadius: '0.5rem' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {taskRoute && (
          <Polyline
            positions={taskRoute}
            pathOptions={{ color: '#1e40af', weight: 2.5, opacity: 0.9 }}
          />
        )}
        {turnpoints?.map((tp, idx) => {
          const color = turnpointColor(tp, idx, lastTpIdx)
          return (
            <Circle
              key={`tp-${idx}`}
              center={[tp.lat, tp.lon]}
              radius={tp.radius}
              pathOptions={{ color, weight: 2, fillColor: color, fillOpacity: 0.08 }}
            >
              <Tooltip permanent direction="top" offset={[0, -4]} className="!bg-white !text-gray-700 !border !border-gray-300 !shadow-sm !text-xs !font-medium !rounded">
                {tp.name ? `${idx + 1}. ${tp.name}` : `TP${idx + 1}`}
              </Tooltip>
            </Circle>
          )
        })}
        {thermals.map((thermal, idx) => (
          <CircleMarker
            key={thermal.id ?? idx}
            center={[thermal.lat, thermal.lon]}
            radius={8 + Math.min(thermal.count ?? 1, 20) * 0.5}
            fillColor={getClimbColor(thermal.avgClimbRate)}
            color="#fff"
            weight={1}
            fillOpacity={0.8}
          >
            <Popup>
              <div className="text-sm space-y-1">
                {thermal.name && (
                  <p className="font-semibold">{thermal.name}</p>
                )}
                <p>
                  <span className="font-medium">Avg climb:</span>{' '}
                  <span style={{ color: getClimbColor(thermal.avgClimbRate) }} className="font-bold">
                    {thermal.avgClimbRate.toFixed(1)} m/s
                  </span>{' '}
                  ({getClimbLabel(thermal.avgClimbRate)})
                </p>
                {thermal.maxClimbRate !== undefined && (
                  <p>
                    <span className="font-medium">Max climb:</span>{' '}
                    {thermal.maxClimbRate.toFixed(1)} m/s
                  </p>
                )}
                {thermal.altitude !== undefined && (
                  <p>
                    <span className="font-medium">Altitude:</span>{' '}
                    {thermal.altitude} m
                  </p>
                )}
                {thermal.count !== undefined && (
                  <p>
                    <span className="font-medium">Observations:</span>{' '}
                    {thermal.count}
                  </p>
                )}
              </div>
            </Popup>
          </CircleMarker>
        ))}
        {(thermals.length > 0 || (turnpoints && turnpoints.length > 0)) && (
          <MapBoundsAdjuster thermals={thermals} routePoints={taskRoute} />
        )}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-12 left-4 z-[1000] bg-white rounded-lg shadow-md border border-gray-200 p-3">
        <p className="text-xs font-semibold text-gray-700 mb-2">Climb Rate</p>
        <div className="space-y-1">
          {LEGEND_ITEMS.map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-gray-600">{item.label} m/s</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
